import axios from "axios";
import { API_URL, withUserSlug } from ".";
import { Token } from "../types/queries";
import { getHeaders } from "./get-headers";
import {
    SetCollectionRulesBodyParams,
    CreateCollectionBodyParams,
    CollectionFilterQueryParams,
    UpdateCollectionBodyParams,
    PinArticleToCollectionBodyParams,
    SetCollectionItemOrderBodyParams,
    CollectionListResponse,
    Collection,
    MainEndpoints,
    CollectionRuleListResponse,
    PublicCollectionShareStatusResponse,
    PublicCollectionResponse
} from "@authory-packages/core";

export interface CommonArgs {
    userSlug: string;
    token: string;
}

interface CreateCollectionArgs extends CommonArgs {
    data: CreateCollectionBodyParams;
    skipNavigate?: boolean;
    showNotification?: boolean;
}

export const createCollection = async ({ token, userSlug, data }: CreateCollectionArgs) => {
    const res = await axios.post<Collection>(
        `${API_URL}${withUserSlug(MainEndpoints.UserCreateCollection, userSlug)}`,
        {
            ...data
        },
        getHeaders(token)
    );

    return res.data;
};
interface deleteCollectionArgs extends CommonArgs {
    collectionId: string;
}

export const deleteCollection = async ({ token, userSlug, collectionId }: deleteCollectionArgs) => {
    const { data } = await axios.post(
        `${API_URL}${withUserSlug(MainEndpoints.UserDeleteCollection, userSlug).replace(":collectionId", encodeURIComponent(collectionId))}`,
        {},
        getHeaders(token)
    );

    return data;
};

export const getCollectionShareStatus = async (slug: string, collectionResolveKey: string = "") => {
    const { data } = await axios.get<PublicCollectionShareStatusResponse>(
        `${API_URL}${withUserSlug(MainEndpoints.UserCollectionPublicShareStatus, slug).replace(":collectionResolveKey", encodeURIComponent(collectionResolveKey))}`
    );

    return data;
};

export const getPublicCollection = async (slug: string, collectionResolveKey: string = "", collectionAccessKey: string = "") => {
    const { data } = await axios.get<PublicCollectionResponse>(
        `${API_URL}${withUserSlug(MainEndpoints.UserCollectionPublic, slug).replace(":collectionResolveKey", encodeURIComponent(collectionResolveKey)).replace(":collectionAccessKey?", encodeURIComponent(collectionAccessKey))}`
    );

    return data;
};

export const getCollections = async (token: Token, userId: string, params: CollectionFilterQueryParams) => {
    const { data } = await axios.get<CollectionListResponse>(`${API_URL}${withUserSlug(MainEndpoints.UserCollections, userId)}`, {
        params,
        ...getHeaders(token!)
    });

    return { ...data, collections: data.collections.sort((a, b) => (a.default ? -1 : b.default ? -1 : 0)) };
};

interface getV3CollectionRulesArgs extends CommonArgs {
    collectionId: string;
}

export const getV3CollectionRules = async ({ userSlug, token, collectionId }: getV3CollectionRulesArgs) => {
    const { data } = await axios.get<CollectionRuleListResponse>(
        `${API_URL}${withUserSlug(MainEndpoints.UserCollectionGetRules, userSlug).replace(":collectionId", collectionId)}`,
        getHeaders(token)
    );

    return data;
};

interface updateV3CollectionRulesProps extends CommonArgs {
    params: SetCollectionRulesBodyParams;
    collectionId: string;
    skipNotification?: boolean;
}

export const updateV3CollectionRules = async ({ token, userSlug, params, collectionId }: updateV3CollectionRulesProps) => {
    const res = await axios.post<SetCollectionRulesBodyParams>(
        `${API_URL}${withUserSlug(MainEndpoints.UserCollectionSetRules, userSlug).replace(":collectionId", collectionId)}`,
        params,
        getHeaders(token)
    );

    return res.data;
};

interface V3UpdateCollectionParams extends CommonArgs {
    params: UpdateCollectionBodyParams;
    collectionId: string;
    useOptimistic?: boolean;
    invalidateContent?: boolean;
    target?: string;
}

export const V3UpdateCollection = async ({ token, userSlug, params, collectionId }: V3UpdateCollectionParams) => {
    const res = await axios.post<Collection>(
        `${API_URL}${withUserSlug(MainEndpoints.UserUpdateCollection, userSlug).replace(":collectionId", encodeURIComponent(collectionId))}`,
        params,
        getHeaders(token)
    );

    return res.data;
};

interface PinToCollectionsParams extends CommonArgs {
    params: PinArticleToCollectionBodyParams;
    collectionId: string;
}

export const PinArticleToCollection = async ({ token, userSlug, params, collectionId }: PinToCollectionsParams) => {
    const res = await axios.post(
        `${API_URL}${withUserSlug(MainEndpoints.UserCollectionPinArticle, userSlug).replace(":collectionId", encodeURIComponent(collectionId))}`,
        params,
        getHeaders(token)
    );

    return res.data;
};

interface setCollectionItemOrderParams extends CommonArgs {
    params: SetCollectionItemOrderBodyParams;
    collectionId: string;
}

export const setCollectionItemOrder = async ({ token, userSlug, params, collectionId }: setCollectionItemOrderParams) => {
    const res = await axios.post(
        `${API_URL}${withUserSlug(MainEndpoints.UserSetCollectionItemOrder, userSlug).replace(":collectionId", encodeURIComponent(collectionId))}`,
        params,
        getHeaders(token)
    );

    return res.data;
};

interface CollectionResetRulesArgs extends CommonArgs {
    collectionId: string;
}

export const collectionResetRules = async ({ userSlug, token, collectionId }: CollectionResetRulesArgs) => {
    const { data } = await axios.post<{}>(
        `${API_URL}${withUserSlug(MainEndpoints.UserCollectionResetOrder, userSlug).replace(":collectionId", collectionId)}`,
        {},
        getHeaders(token)
    );

    return data;
};

export const getPublicSmartShareCollection = async (userSlug: string, smartShareURL: string = "") => {
    const { data } = await axios.get<PublicCollectionResponse>(
        `${API_URL}${withUserSlug(MainEndpoints.UserSmartShareCollection, userSlug).replace(":smartShareURL", encodeURIComponent(smartShareURL))}`,
        undefined
    );

    return data;
};
